import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FiArrowUpRight, FiCheckCircle, FiGitPullRequest } from "react-icons/fi";

import { AppWrap, MotionWrap } from "../../wrapper";
import "./CodeLab.scss";

const tasks = [
  {
    id: "landing",
    label: "Landing page",
    status: "Building",
    title: "Portfolio refresh",
    prompt: "Refine the hero, tighten selected work, and keep the page aligned with a clean product-site standard.",
    files: [
      { name: "src/container/Header.jsx", diff: "+42 −8" },
      { name: "src/container/Work.scss", diff: "+64 −21" },
    ],
    logs: [
      "Reading current layout tokens",
      "Applying warmer surface colors",
      "Checking responsive card rhythm",
      "Preparing production build",
    ],
  },
  {
    id: "dashboard",
    label: "Dashboard UI",
    status: "Reviewing",
    title: "Product interface review",
    prompt: "Audit the dashboard states, card density, empty views, and mobile spacing before launch.",
    files: [
      { name: "src/components/Table.jsx", diff: "+28 −10" },
      { name: "src/styles/dashboard.scss", diff: "+91 −34" },
    ],
    logs: [
      "Mapping status filters",
      "Testing empty and loading states",
      "Reducing layout shift",
      "Writing final UI notes",
    ],
  },
  {
    id: "content",
    label: "Content flow",
    status: "Shipping",
    title: "Editable content workflow",
    prompt: "Keep content flexible, protect fallback states, and make project filters resilient.",
    files: [
      { name: "src/client.js", diff: "+12 −2" },
      { name: "src/container/Work.jsx", diff: "+37 −9" },
    ],
    logs: [
      "Normalizing project categories",
      "Syncing fallback data",
      "Validating content response",
      "Deploy checklist ready",
    ],
  },
];

const CodeLab = () => {
  const constraintsRef = useRef(null);
  const [activeTask, setActiveTask] = useState(tasks[0]);
  const [frontWindow, setFrontWindow] = useState("desktop");
  const [canDragWindows, setCanDragWindows] = useState(false);

  const bringToFront = (windowName) => setFrontWindow(windowName);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 901px) and (pointer: fine)");
    const updateDragMode = () => setCanDragWindows(media.matches);

    updateDragMode();

    if (media.addEventListener) {
      media.addEventListener("change", updateDragMode);
      return () => media.removeEventListener("change", updateDragMode);
    }

    media.addListener(updateDragMode);
    return () => media.removeListener(updateDragMode);
  }, []);

  return (
    <div className="lab">
      <div className="app__section-head">
        <motion.div
          className="app__section-title"
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          <span className="app__kicker">
            <em>03</em>Studio
          </span>
          <h2>
            A working view of how interfaces get <em>made.</em>
          </h2>
        </motion.div>

        <motion.div
          className="lab__lede"
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          <p className="app__section-lede">
            Planning, implementation, review, and launch polish &mdash; kept
            visible while the work moves.
          </p>
          <span className="lab__badge">
            <i aria-hidden="true" />
            3 tasks in motion
          </span>
        </motion.div>
      </div>

      <motion.div
        className="lab__stage"
        ref={constraintsRef}
        initial={{ opacity: 0, y: 34 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        viewport={{ once: true, amount: 0.15 }}
      >
        <span className="lab__stage-note" aria-hidden="true">
          drag the windows
        </span>

        <motion.div
          className={`lab__window lab__window--desktop${frontWindow === "desktop" ? " is-front" : ""}`}
          drag={canDragWindows}
          dragConstraints={constraintsRef}
          dragMomentum={false}
          onMouseDown={() => bringToFront("desktop")}
          onFocus={() => bringToFront("desktop")}
          tabIndex={0}
          initial={{ x: 0, y: 0 }}
        >
          <div className="lab__chrome">
            <span className="lab__chrome-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <strong>frontend-workspace</strong>
            <em>main</em>
          </div>

          <div className="lab__desktop-grid">
            <aside className="lab__queue">
              <span className="lab__queue-title">Running tasks</span>
              {tasks.map((task) => (
                <button
                  type="button"
                  key={task.id}
                  className={activeTask.id === task.id ? "is-active" : ""}
                  onClick={() => {
                    setActiveTask(task);
                    bringToFront("desktop");
                  }}
                >
                  <i className="lab__dot" />
                  <span>
                    <strong>{task.label}</strong>
                    <small>{task.status}</small>
                  </span>
                </button>
              ))}
            </aside>

            <main className="lab__task">
              <div className="lab__tabs" role="tablist" aria-label="Studio tasks">
                {tasks.map((task) => (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTask.id === task.id}
                    key={task.id}
                    className={activeTask.id === task.id ? "is-active" : ""}
                    onClick={() => setActiveTask(task)}
                  >
                    {task.label}
                  </button>
                ))}
              </div>

              <div className="lab__prompt">
                <small>{activeTask.status}</small>
                <h3>{activeTask.title}</h3>
                <p>{activeTask.prompt}</p>
              </div>

              <div className="lab__files">
                {activeTask.files.map((file) => (
                  <button type="button" key={file.name}>
                    <FiGitPullRequest aria-hidden="true" />
                    <span>{file.name}</span>
                    <strong>{file.diff}</strong>
                  </button>
                ))}
              </div>

              <div className="lab__composer">
                <span>Plan, review, refine the interface&hellip;</span>
                <button type="button" aria-label="Submit task">
                  <FiArrowUpRight />
                </button>
              </div>
            </main>

            <section className="lab__preview">
              <div className="lab__preview-bar">
                <span>localhost:3000</span>
                <FiCheckCircle aria-hidden="true" />
              </div>
              <div className="lab__preview-card">
                <small>Interface preview</small>
                <h4>{activeTask.title}</h4>
                <p>Responsive layout, clean states, stable filters, and launch-ready spacing.</p>
                <a href="#work" className="app__textlink">
                  See projects
                  <FiArrowUpRight aria-hidden="true" />
                </a>
              </div>
            </section>
          </div>
        </motion.div>

        <motion.div
          className={`lab__window lab__window--cli${frontWindow === "cli" ? " is-front" : ""}`}
          drag={canDragWindows}
          dragConstraints={constraintsRef}
          dragMomentum={false}
          onMouseDown={() => bringToFront("cli")}
          onFocus={() => bringToFront("cli")}
          tabIndex={0}
          initial={{ x: 0, y: 0 }}
        >
          <div className="lab__chrome lab__chrome--dark">
            <span className="lab__chrome-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <strong>build console &mdash; zsh</strong>
          </div>

          <div className="lab__terminal">
            <div className="lab__terminal-head">
              <span className="lab__pulse" aria-hidden="true" />
              <strong>{activeTask.status}&hellip;</strong>
            </div>

            {activeTask.logs.map((line, index) => (
              <div className="lab__terminal-line" key={`${activeTask.id}-${line}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{line}</p>
                <i aria-hidden="true" />
              </div>
            ))}

            <div className="lab__terminal-input">
              <span>$</span>
              <strong>quality check</strong>
              <i aria-hidden="true" />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AppWrap(
  MotionWrap(CodeLab, "app__code-lab"),
  "studio",
  "app__band--stone"
);
